import { Checkbox, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import ProvidersFormatsRow from './ProvidersFormatsRow';

const ROW_HEIGHT = 54;

const getProviderKey = (p) => p.code?.toString();

const ProvidersFormatsTablePreview = ({
  articles,
  newProvidersFormats,
  editedProvidersFormats,
  withOutChangesProvidersFormats,
  withIssuesProvidersFormats,
  selectedKeys,
  onToggle,
  onToggleAll,
}) => {
  const parentRef = useRef(null);

  const [providerExcelTypeFilter, setProviderExcelTypeFilter] = useState(['created', 'edited', 'with-issues']);

  const filteredProviders = useMemo(() => {
    let filtered = [];

    if (providerExcelTypeFilter.length > 0) {
      if (providerExcelTypeFilter.includes('with-issues')) {
        filtered = [...filtered, ...withIssuesProvidersFormats];
      }
      if (providerExcelTypeFilter.includes('created')) {
        filtered = [...filtered, ...newProvidersFormats];
      }
      if (providerExcelTypeFilter.includes('edited')) {
        filtered = [...filtered, ...editedProvidersFormats];
      }
      if (providerExcelTypeFilter.includes('with-out-changes')) {
        filtered = [...filtered, ...withOutChangesProvidersFormats];
      }
    }

    return filtered;
  }, [
    newProvidersFormats,
    editedProvidersFormats,
    withOutChangesProvidersFormats,
    withIssuesProvidersFormats,
    providerExcelTypeFilter,
  ]);

  const providerMapping = useMemo(() => {
    const mapping = {};
    newProvidersFormats.forEach((p) => { mapping[getProviderKey(p)] = 'created'; });
    editedProvidersFormats.forEach((p) => { mapping[getProviderKey(p)] = 'edited'; });
    withOutChangesProvidersFormats.forEach((p) => { mapping[getProviderKey(p)] = 'with-out-changes'; });
    withIssuesProvidersFormats.forEach((p) => { mapping[getProviderKey(p)] = 'with-issues'; });
    return mapping;
  }, [newProvidersFormats, editedProvidersFormats, withOutChangesProvidersFormats, withIssuesProvidersFormats]);

  const selectableFilteredKeys = useMemo(
    () => filteredProviders
      .filter((p) => {
        const type = providerMapping[getProviderKey(p)];
        return type !== 'with-issues' && type !== 'with-out-changes';
      })
      .map(getProviderKey),
    [filteredProviders, providerMapping],
  );

  const allSelected = selectableFilteredKeys.length > 0 && selectableFilteredKeys.every((k) => selectedKeys?.has(k));
  const someSelected = selectableFilteredKeys.some((k) => selectedKeys?.has(k));

  const rowVirtualizer = useVirtualizer({
    count: filteredProviders.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  return (
    <>
      <div className="articles-table-filter-container">
        <div
          style={{
            width: '20rem',
          }}
        >
          <InputLabel size="small" id="providerType-label">
            Tipo proveedor
          </InputLabel>
          <br />
          <Select
            style={{
              width: '20rem',
            }}
            size="small"
            id="providers-multiple-checkbox"
            labelId="providerType-label"
            multiple
            value={providerExcelTypeFilter}
            onChange={(e) => setProviderExcelTypeFilter(e.target.value)}
            input={<OutlinedInput label="providerType-label" />}
            renderValue={(selected) => {
              const statusMapping = {
                created: 'Por Crear',
                edited: 'Por editar',
                'with-out-changes': 'Sin cambios',
                'with-issues': 'Con problemas',
              };
              return selected.map((s) => statusMapping[s]).join(', ');
            }}
          >
            <MenuItem value="created">
              <Checkbox checked={providerExcelTypeFilter.includes('created')} />
              <ListItemText primary={`Por Crear (${newProvidersFormats.length})`} />
            </MenuItem>
            <MenuItem value="edited">
              <Checkbox checked={providerExcelTypeFilter.includes('edited')} />
              <ListItemText primary={`Por editar (${editedProvidersFormats.length})`} />
            </MenuItem>
            <MenuItem value="with-out-changes">
              <Checkbox checked={providerExcelTypeFilter.includes('with-out-changes')} />
              <ListItemText primary={`Sin cambios (${withOutChangesProvidersFormats.length})`} />
            </MenuItem>
            <MenuItem value="with-issues">
              <Checkbox checked={providerExcelTypeFilter.includes('with-issues')} />
              <ListItemText primary={`Con problemas (${withIssuesProvidersFormats.length})`} />
            </MenuItem>
          </Select>
        </div>
      </div>
      <div className="articles-table-major-container">
        <div className="articles-table-inner">
          <table className="table-preview table-providers-preview table-header">
            <thead>
              {filteredProviders.length > 0 && (
                <tr>
                  <th>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => onToggleAll?.(selectableFilteredKeys, e.target.checked)}
                    />
                  </th>
                  <th>CODIGO ARTICULO</th>
                  <th>CODIGO FORMATO</th>
                  <th>CODIGO ARIBA</th>
                  <th>PROVEEDOR</th>
                  <th>PRECIO</th>
                  <th>ACTIVO</th>
                </tr>
              )}
            </thead>
          </table>

          <div ref={parentRef} className="table-content-articles-container">
            <table className="table-preview table-providers-preview table-body">
              {filteredProviders.length > 0 ? (
                <tbody
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    position: 'relative',
                    display: 'block',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = filteredProviders[virtualRow.index];
                    if (!row) return null;

                    const article = articles.find((a) => a.code === row.codeArticle);
                    const providerKey = getProviderKey(row);
                    row.type = providerMapping[providerKey];

                    return (
                      <ProvidersFormatsRow
                        key={`${providerKey}-${virtualRow.index}`}
                        providerFormat={row}
                        index={virtualRow.index}
                        article={article}
                        isSelected={selectedKeys?.has(providerKey)}
                        onToggle={() => onToggle?.(providerKey)}
                        isDisabled={row.type === 'with-issues'}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      />
                    );
                  })}
                </tbody>
              ) : (
                <tbody style={{ height: `10rem` }}>
                  <tr style={{ height: `10rem` }}>
                    <span
                      style={{
                        color: '#6e6b6b',
                        fontSize: '1.2rem',
                        fontStyle: 'italic',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '1rem 2rem',
                      }}
                    >
                      No hay proveedores para mostrar
                    </span>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProvidersFormatsTablePreview;
